/**
 * The expected-mechanisms value: which predefined mechanisms are selected, plus one
 * free-text entry.
 *
 * expected_mechanisms is a json column, so it holds this object directly. It previously
 * held a flat string[] with the free-text entry encoded as `custom:${text}` alongside the
 * predefined ids, which broke in two ways: a custom mechanism whose own text began with
 * "custom:" did not round-trip, and any future predefined id starting with "custom"
 * would collide with the marker.
 *
 * Reading still accepts the flat array, because rows written before this change hold it.
 * Writing always produces the object.
 */
export type ExpectedMechanisms = {
  selected: string[];
  custom: string;
};

/**
 * The mechanisms an experiment can be expected to work through. Shared by the hypothesis
 * card, which offers them, and the programme screen, which reports win rate by them.
 */
export const PREDEFINED_MECHANISMS = [
  {
    id: 'attention',
    label: 'Increased attention / visibility',
    description: "The change is expected to draw the user's attention more effectively to an element or action.",
  },
  {
    id: 'clarity',
    label: 'Improved clarity of next step',
    description: 'The change makes it clearer what the user should do next.',
  },
  {
    id: 'cognitive',
    label: 'Reduced cognitive load',
    description: 'The change simplifies the interface or decision, making it easier to process.',
  },
  {
    id: 'friction',
    label: 'Reduced friction',
    description: 'The change removes effort, steps, or perceived difficulty in completing the action.',
  },
  {
    id: 'value',
    label: 'Increased perceived value or trust',
    description: 'The change increases confidence, credibility, or perceived benefit of taking the action.',
  },
] as const;

export const EMPTY_MECHANISMS: ExpectedMechanisms = { selected: [], custom: '' };

/** What the free-text mechanism is called wherever mechanisms are counted: Program and the Library. */
export const CUSTOM_MECHANISM_LABEL = 'Custom mechanism';

const MECHANISM_LABELS = new Map<string, string>(
  PREDEFINED_MECHANISMS.map((mechanism) => [mechanism.id, mechanism.label]),
);

/** A predefined mechanism's label, or the id itself for one no longer predefined. */
export const mechanismLabel = (id: string): string => MECHANISM_LABELS.get(id) ?? id;

const LEGACY_CUSTOM_PREFIX = 'custom:';

/** Rows written before the object shape: ids and one `custom:`-prefixed entry. */
const parseLegacyArray = (raw: readonly unknown[]): ExpectedMechanisms => {
  const selected: string[] = [];
  let custom = '';

  for (const item of raw) {
    if (typeof item !== 'string') continue;

    if (item.startsWith(LEGACY_CUSTOM_PREFIX)) {
      custom = item.slice(LEGACY_CUSTOM_PREFIX.length);
    } else {
      selected.push(item);
    }
  }

  return { selected, custom };
};

export const parseMechanisms = (raw: unknown): ExpectedMechanisms => {
  if (!raw) return EMPTY_MECHANISMS;
  if (Array.isArray(raw)) return parseLegacyArray(raw);

  if (typeof raw === 'object') {
    const { selected, custom } = raw as Partial<ExpectedMechanisms>;
    return {
      selected: Array.isArray(selected) ? selected.filter((id): id is string => typeof id === 'string') : [],
      custom: typeof custom === 'string' ? custom : '',
    };
  }

  return EMPTY_MECHANISMS;
};

/** Trims the free text, so whitespace alone is stored as no custom mechanism. */
export const serializeMechanisms = (mechanisms: ExpectedMechanisms): ExpectedMechanisms => ({
  selected: [...mechanisms.selected],
  custom: mechanisms.custom.trim(),
});

/** Content equality, used to tell a real server change from a refetch of the same value. */
export const sameMechanisms = (a: ExpectedMechanisms, b: ExpectedMechanisms): boolean =>
  a.custom === b.custom && a.selected.length === b.selected.length && a.selected.every((id, i) => id === b.selected[i]);
